import BaseStepHandler from './BaseStepHandler';
import { Image, Circle, Rect } from 'fabric';

/**
 * Handler for select step type (options with images)
 */
export default class SelectStepHandler extends BaseStepHandler {
    /**
     * Create a new SelectStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.originalDimensions = null;
        this.modificationHandler = null;
        this.shapeMask = null; // Store reference to the shape mask
        this.DPI = 900; // Dots per inch for mm to px conversion
    }

    /**
     * Initialize select elements
     */
    initialize() {
        super.initialize();
        
        // Get the main element ID from definition
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        // Get element from canvas
        let element = this.canvas.getElement(elementId);
        
        // Element should already exist from template
        if (element) {
            // Store reference
            this.elements.set(elementId, element);
            
            // Store original dimensions for later use when changing images
            this.originalDimensions = {
                width: element.width * element.scaleX,
                height: element.height * element.scaleY,
                scaleX: element.scaleX,
                scaleY: element.scaleY
            };
        }
        
        return this;
    }
    
    /**
     * Process template when no data exists
     */
    processTemplate() {
        const stepData = this.getStepData();
        
        // If we have data, update with it (will handle visibility)
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
        } else {
            // Otherwise hide element for optional steps
            if (this.definition.required !== true) {
                this.hide();
            } else {
                this.show();
            }
        }
        
        return this;
    }

    /**
     * Activate step
     */
    activate() {
        super.activate();
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Make element interactive based on allowed actions
        element.set({
            selectable: true,
            hasControls: this.definition.allowedActions?.includes('scale') ?? false,
            lockMovementX: !(this.definition.allowedActions?.includes('move') ?? false),
            lockMovementY: !(this.definition.allowedActions?.includes('move') ?? false),
            cornerStyle: 'circle',
            transparentCorners: false,
            cornerColor: '#0d6efd',
            padding: 0
        });
        
        // Make active on canvas
        this.canvas.canvas.setActiveObject(element);
        this.canvas.canvas.requestRenderAll();
        
        // Attach modification listener
        this.attachModificationListener(element);
        
        return this;
    }
    
    /**
     * Deactivate step
     */
    deactivate() {
        super.deactivate();
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Remove interactivity
        element.set({
            selectable: false,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true
        });
        
        // Detach modification listener
        this.detachModificationListener(element);
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Update element with step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // If not enabled or no selection, hide the element
        if (!stepData || !stepData.enabled) {
            this.hide();
            return this;
        }

        // Show the element
        this.show();

        // Apply shape if available
        if (stepData.selectedShape) {
            this.applyShape(stepData.selectedShape);
        } else if (this.definition.shapes && this.definition.shapes.length > 0) {
            // Default to first shape if none selected
            this.applyShape(this.definition.shapes[0]);
        }

        // If no texture selection, just show with shape mask
        if (!stepData.selected) return this;
        
        // Apply selected texture option
        this.applyOption(stepData.selected);
        
        // Apply stored position and scale if available
        this.applyPositionAndScale(stepData);
        
        return this;
    }
    
    /**
     * Apply position and scale from step data
     * 
     * @param {object} stepData - Step data
     */
    applyPositionAndScale(stepData) {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Apply position if available
        if (stepData.left !== undefined) element.set('left', stepData.left);
        if (stepData.top !== undefined) element.set('top', stepData.top);
        
        // Apply scale if available
        if (stepData.scaleX !== undefined) element.set('scaleX', stepData.scaleX);
        if (stepData.scaleY !== undefined) element.set('scaleY', stepData.scaleY);
        
        this.canvas.canvas.requestRenderAll();
    }
    
    /**
     * Convert mm to pixels at 300 DPI
     * 
     * @param {number} mm - Value in millimeters
     * @returns {number} - Value in pixels
     */
    mmToPixels(mm) {
        return (mm / 25.4) * this.DPI;
    }

    /**
     * Get shape dimensions in pixels
     * 
     * @param {object} shapeData - Shape data
     * @returns {object} - Width and height in pixels
     */
    getShapeDimensions(shapeData) {
        if (shapeData.type === 'rect') {
            return {
                width: this.mmToPixels(shapeData.dimensions.width),
                height: this.mmToPixels(shapeData.dimensions.height)
            };
        } else if (shapeData.type === 'circle') {
            const diameter = this.mmToPixels(shapeData.dimensions.radius) * 2;
            return {
                width: diameter,
                height: diameter
            };
        }
        return { width: 0, height: 0 };
    }

    /**
     * Create and apply shape mask
     * 
     * @param {object} shapeData - Shape data
     */
    applyShape(shapeData) {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;

        // Remove existing mask if any
        if (element.clipPath) {
            element.clipPath = null;
        }
        
        // Create new shape based on type
        let shapeMask;
        
        if (shapeData.type === 'rect') {
            // Convert mm to pixels
            const width = this.mmToPixels(shapeData.dimensions.width);
            const height = this.mmToPixels(shapeData.dimensions.height);
            
            shapeMask = new Rect({
                width: width,
                height: height,
                originX: 'center',
                originY: 'center',
                fill: 'white',
                absolutePositioned: true
            });
        } else if (shapeData.type === 'circle') {
            // Convert mm to pixels
            const radius = this.mmToPixels(shapeData.dimensions.radius);
            
            shapeMask = new Circle({
                radius: radius,
                originX: 'center',
                originY: 'center',
                fill: 'white',
                absolutePositioned: true
            });
        }

        if (!shapeMask) return;
        
        // Position at element center
        shapeMask.set({
            left: element.left,
            top: element.top
        });
        
        // Apply as clipPath
        element.clipPath = shapeMask;
        this.shapeMask = shapeMask;
        
        // Scale the element to ensure it covers the shape
        this.ensureElementFitsShape(element, shapeData);
        
        // Update step data with selected shape
        const stepData = this.getStepData() || {};
        stepData.selectedShape = shapeData;
        this.setStepData(stepData, false);
        
        this.canvas.canvas.requestRenderAll();
    }
    
    /**
     * Ensure element properly fits the shape mask
     * 
     * @param {object} element - Fabric element
     * @param {object} shapeData - Shape data
     */
    ensureElementFitsShape(element, shapeData) {
        // Get shape dimensions
        const shapeDimensions = this.getShapeDimensions(shapeData);
        
        // Calculate scaling needed to fit the shape
        const scaleX = shapeDimensions.width / element.width;
        const scaleY = shapeDimensions.height / element.height;
        
        // Use the larger scale to ensure the element covers the shape completely
        const scale = Math.max(scaleX, scaleY);
        
        // Apply scaling to ensure coverage
        element.set({
            scaleX: scale,
            scaleY: scale
        });
    }
    
    /**
     * Find option by ID across all groups
     * 
     * @param {string} optionId - Option ID to find
     * @returns {object|null} - Found option or null
     */
    findOptionById(optionId) {
        // First, check if we have option groups defined
        if (this.definition.optionGroups) {
            // Look through each group
            for (const group of this.definition.optionGroups) {
                const option = group.options.find(opt => opt.id === optionId);
                if (option) {
                    return {
                        option,
                        group
                    };
                }
            }
        }
        
        // Fallback to flat options array if optionGroups not found or option not in groups
        if (this.definition.options) {
            const option = this.definition.options.find(opt => opt.id === optionId);
            if (option) {
                return {
                    option,
                    group: null
                };
            }
        }
        
        return null;
    }
    
    /**
     * Apply selected option to element
     * 
     * @param {object} selectedOption - Selected option data
     */
    applyOption(selectedOption) {
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Get asset path
        const imagePath = `/wp-content/plugins/octo-stc/public/assets/${selectedOption.src}`;
        
        // Store clipPath to reapply
        const currentClipPath = element.clipPath;
        
        // Get current shape data for scaling calculation
        const stepData = this.getStepData();
        const shapeData = stepData?.selectedShape || 
                        (this.definition.shapes && this.definition.shapes.length > 0 ? 
                        this.definition.shapes[0] : null);
        
        // Set a loading flag for later use
        element._isCustomImageLoading = true;
        
        // Load and apply the image
        element.setSrc(imagePath).then(() => {
            // Reapply clipPath if exists
            if (currentClipPath) {
                element.clipPath = currentClipPath;
            }
            
            // Ensure element fits shape
            if (shapeData) {
                this.ensureElementFitsShape(element, shapeData);
            } else if (this.originalDimensions) {
                // Fallback to original dimensions if no shape
                const scaleX = this.originalDimensions.width / element.width;
                const scaleY = this.originalDimensions.height / element.height;
                
                // Use the larger scale to ensure coverage
                const scale = Math.max(scaleX, scaleY);
                
                element.set({
                    scaleX: scale,
                    scaleY: scale
                });
            }
            
            // Clear loading flag
            element._isCustomImageLoading = false;
            
            this.canvas.canvas.requestRenderAll();
        });
    }
    
    /**
     * Attach modification listener to track changes
     * 
     * @param {object} element - Fabric object
     */
    attachModificationListener(element) {
        // Remove existing listener if any
        this.detachModificationListener(element);
        
        // Create multiple handlers for different events
        this.movingHandler = () => {
            this.updateClipPathPosition(element);
        };
        
        this.modifiedHandler = () => {
            this.handleElementModified(element);
        };
        
        // Attach to multiple events
        element.on('moving', this.movingHandler);
        element.on('scaling', this.movingHandler); // Also track scaling
        element.on('rotating', this.movingHandler); // Also track rotation
        element.on('modified', this.modifiedHandler);
    }

    updateClipPathPosition(element) {
        if (element.clipPath) {
            element.clipPath.set({
                left: element.left,
                top: element.top
            });
            this.canvas.canvas.requestRenderAll();
        }
    }
    
    /**
     * Detach modification listener
     * 
     * @param {object} element - Fabric object
     */
    detachModificationListener(element) {
        if (this.modifiedHandler) {
            element.off('modified', this.modifiedHandler);
            this.modifiedHandler = null;
        }
        
        if (this.movingHandler) {
            element.off('moving', this.movingHandler);
            element.off('scaling', this.movingHandler);
            element.off('rotating', this.movingHandler);
            this.movingHandler = null;
        }
    }
    
    /**
     * Handle element modification (move/scale)
     * 
     * @param {object} element - Modified fabric object
     */
    handleElementModified(element) {
        // Get current step data
        const stepData = this.getStepData() || {};
        
        // Update position and scale
        stepData.left = element.left;
        stepData.top = element.top;
        stepData.scaleX = element.scaleX;
        stepData.scaleY = element.scaleY;
        
        // Update the original dimensions to match user modifications
        if (this.originalDimensions) {
            this.originalDimensions.width = element.width * element.scaleX;
            this.originalDimensions.height = element.height * element.scaleY;
        }
        
        // If we have a clipPath, update its position to match element
        if (element.clipPath) {
            element.clipPath.set({
                left: element.left,
                top: element.top
            });
        }
        
        // Update step data without triggering recursive update
        this.setStepData(stepData, false);
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData() || {};
        const price = this.definition.price || 0;
        const shapes = this.definition.shapes || [];
        const hasOptionGroups = !!this.definition.optionGroups && this.definition.optionGroups.length > 0;
        
        // Determine selected shape - default to first if none selected
        const selectedShapeId = stepData.selectedShape?.id || (shapes.length > 0 ? shapes[0].id : null);
        
        // Determine selected option and group
        let selectedGroup = null;
        let selectedOption = null;
        
        if (stepData.selected) {
            const found = this.findOptionById(stepData.selected.id);
            if (found) {
                selectedOption = found.option;
                selectedGroup = found.group;
            }
        }
        
        // Determine default selected group (use first if none selected)
        const defaultGroup = selectedGroup || 
            (hasOptionGroups ? this.definition.optionGroups[0] : null);
        
        return `
            <div class="stc-select-field">
                <div class="stc-select-header">
                    <label class="stc-switch">
                        <input type="checkbox" 
                                class="stc-select-toggle"
                                ${stepData?.enabled ? 'checked' : ''} 
                                data-price="${price}">
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">
                        ${this.definition.title}
                        ${price ? `<span class="stc-price">(+$${price})</span>` : ''}
                    </span>
                </div>
    
                <div class="stc-select-options ${stepData?.enabled ? 'is-enabled' : ''}">
                    ${shapes.length > 0 ? `
                    <h4 class="stc-shape-title">Select Shape</h4>
                    <div class="stc-shape-options">
                        ${shapes.map(shape => `
                            <div class="stc-select-option stc-shape-option ${selectedShapeId === shape.id ? 'is-selected' : ''}" 
                                    data-shape-id="${shape.id}">
                                <div class="stc-select-option-image stc-shape-preview">
                                    ${shape.type === 'rect' ? 
                                    `<div class="stc-shape-rect" style="width: ${shape.dimensions.width / 2}px; height: ${shape.dimensions.height / 2}px;"></div>` : 
                                    `<div class="stc-shape-circle" style="width: ${shape.dimensions.radius * 2 / 2}px; height: ${shape.dimensions.radius * 2 / 2}px;"></div>`}
                                </div>
                                <div class="stc-select-option-title">${shape.title}</div>
                            </div>
                        `).join('')}
                    </div>
                    <div class="stc-shape-texture-divider"></div>
                    ` : ''}
                    
                    ${hasOptionGroups ? `
                    <div class="stc-option-group-selector">
                        <h4 class="stc-texture-category-title">Texture Category</h4>
                        <select class="stc-font-select">
                            ${this.definition.optionGroups.map(group => `
                                <option value="${group.id}" ${defaultGroup && group.id === defaultGroup.id ? 'selected' : ''}>
                                    ${group.title}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                    
                    <h4 class="stc-texture-title">Select Texture</h4>
                    ${this.definition.optionGroups.map(group => `
                        <div class="stc-texture-options stc-texture-group" 
                             data-group-id="${group.id}" 
                             style="display: ${defaultGroup && group.id === defaultGroup.id ? 'grid' : 'none'}">
                            ${group.options.map(option => `
                                <div class="stc-select-option ${stepData?.selected?.id === option.id ? 'is-selected' : ''}" 
                                     data-option-id="${option.id}" data-group-id="${group.id}">
                                    <div class="stc-select-option-image">
                                        <img src="/wp-content/plugins/octo-stc/public/assets/${option.src}" alt="${option.title}">
                                    </div>
                                    <div class="stc-select-option-title">${option.title}</div>
                                </div>
                            `).join('')}
                        </div>
                    `).join('')}
                    ` : `
                    <h4 class="stc-texture-title">Select Texture</h4>
                    <div class="stc-texture-options">
                        ${this.definition.options.map(option => `
                            <div class="stc-select-option ${stepData?.selected?.id === option.id ? 'is-selected' : ''}" 
                                 data-option-id="${option.id}">
                                <div class="stc-select-option-image">
                                    <img src="/wp-content/plugins/octo-stc/public/assets/${option.src}" alt="${option.title}">
                                </div>
                                <div class="stc-select-option-title">${option.title}</div>
                            </div>
                        `).join('')}
                    </div>
                    `}
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const toggleSwitch = this.panelElement.querySelector('.stc-select-toggle');
        const optionsContainer = this.panelElement.querySelector('.stc-select-options');
        const shapeOptions = this.panelElement.querySelectorAll('.stc-shape-option');
        const groupSelect = this.panelElement.querySelector('.stc-font-select');
        const textureGroups = this.panelElement.querySelectorAll('.stc-texture-group');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        
        // Bind toggle switch
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Toggle options container
                if (optionsContainer) {
                    optionsContainer.classList.toggle('is-enabled', stepData.enabled);
                }
                
                // Update clear button
                if (clearButton) {
                    clearButton.disabled = !stepData.enabled;
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind shape option selection
        shapeOptions.forEach(option => {
            this.addEventListener(option, 'click', () => {
                // Get shape ID
                const shapeId = option.dataset.shapeId;
                if (!shapeId) return;
                
                // Update selection in UI
                shapeOptions.forEach(opt => {
                    opt.classList.toggle('is-selected', opt.dataset.shapeId === shapeId);
                });
                
                // Find selected shape data
                const selectedShape = this.definition.shapes.find(shape => shape.id === shapeId);
                if (!selectedShape) return;
                
                // Update step data
                const stepData = this.getStepData() || { enabled: true };
                stepData.selectedShape = selectedShape;
                
                // Apply the shape
                this.applyShape(selectedShape);
                
                this.setStepData(stepData);
            });
        });
        
        // Bind group selector
        if (groupSelect) {
            this.addEventListener(groupSelect, 'change', () => {
                const selectedGroupId = groupSelect.value;
                
                // Hide all groups and show selected
                textureGroups.forEach(group => {
                    const isSelected = group.dataset.groupId === selectedGroupId;
                    group.style.display = isSelected ? 'grid' : 'none';
                });
            });
        }
        
        // Bind texture option selection - for all option groups
        textureGroups.forEach(group => {
            const textureOptions = group.querySelectorAll('.stc-select-option');
            
            textureOptions.forEach(option => {
                this.addEventListener(option, 'click', () => {
                    // Get option data
                    const optionId = option.dataset.optionId;
                    if (!optionId) return;
                    
                    // Update selection UI in this group only
                    textureOptions.forEach(opt => {
                        opt.classList.toggle('is-selected', opt.dataset.optionId === optionId);
                    });
                    
                    // Also update any options with same ID in other groups
                    textureGroups.forEach(otherGroup => {
                        if (otherGroup !== group) {
                            const sameIdOption = otherGroup.querySelector(`[data-option-id="${optionId}"]`);
                            if (sameIdOption) {
                                // Clear other selections in that group
                                otherGroup.querySelectorAll('.stc-select-option').forEach(opt => {
                                    opt.classList.remove('is-selected');
                                });
                                // Select this option
                                sameIdOption.classList.add('is-selected');
                            }
                        }
                    });
                    
                    // Find the selected option
                    const found = this.findOptionById(optionId);
                    if (!found || !found.option) return;
                    
                    // Update step data
                    const stepData = this.getStepData() || { enabled: true };
                    stepData.selected = found.option;
                    
                    // Make sure we have a shape selected
                    if (!stepData.selectedShape && this.definition.shapes && this.definition.shapes.length > 0) {
                        stepData.selectedShape = this.definition.shapes[0];
                    }
                    
                    this.setStepData(stepData);
                });
            });
        });
        
        // For backward compatibility - bind direct texture options if any
        const directOptions = this.panelElement.querySelectorAll('.stc-texture-options:not(.stc-texture-group) .stc-select-option');
        directOptions.forEach(option => {
            this.addEventListener(option, 'click', () => {
                // Get option data
                const optionId = option.dataset.optionId;
                if (!optionId) return;
                
                // Update selection in UI
                directOptions.forEach(opt => {
                    opt.classList.toggle('is-selected', opt.dataset.optionId === optionId);
                });
                
                // Find the option in the flat options list
                const selectedOption = this.definition.options.find(opt => opt.id === optionId);
                if (!selectedOption) return;
                
                // Update step data
                const stepData = this.getStepData() || { enabled: true };
                stepData.selected = selectedOption;
                
                // Make sure we have a shape selected
                if (!stepData.selectedShape && this.definition.shapes && this.definition.shapes.length > 0) {
                    stepData.selectedShape = this.definition.shapes[0];
                }
                
                this.setStepData(stepData);
            });
        });
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }

    /**
     * Step-specific reset logic
     */
    onReset() {
        // Reset selection and appearance
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Get original data from template
        const elementData = this.canvas.getElementData(elementId);
        if (!elementData || !elementData.data) return;
        
        // Remove clipPath (shape mask)
        if (element.clipPath) {
            element.clipPath = null;
        }
        
        // Reset to original image if available
        if (elementData.data.src) {
            element.setSrc(elementData.data.src).then(() => {
                // Reset to original dimensions
                if (this.originalDimensions) {
                    element.set({
                        scaleX: this.originalDimensions.scaleX,
                        scaleY: this.originalDimensions.scaleY
                    });
                }
                
                this.canvas.canvas.requestRenderAll();
            });
        }
    }

    /**
     * Get export data for select step 
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const baseData = super.getStepExportData();
        const stepData = this.getStepData();
        
        // Find full option data if only ID is stored
        let selectedOption = stepData.selected;
        let selectedGroup = null;
        
        if (selectedOption) {
            const found = this.findOptionById(selectedOption.id);
            if (found) {
                selectedOption = found.option;
                selectedGroup = found.group;
            }
        }
        
        // Enhanced export data with save operation context
        return {
            ...baseData,
            configRef: stepData.configReferenceId,
            patchConfiguration: {
                selectedTexture: selectedOption ? {
                    id: selectedOption.id,
                    title: selectedOption.title,
                    src: selectedOption.src,
                    assetPath: `/wp-content/plugins/octo-stc/public/assets/${selectedOption.src}`
                } : null,
                selectedShape: stepData.selectedShape,
                group: selectedGroup,
                positioning: {
                    left: stepData.left,
                    top: stepData.top,
                    scaleX: stepData.scaleX,
                    scaleY: stepData.scaleY
                }
            },
            selectContent: {
                selectedOption: selectedOption ? {
                    id: selectedOption.id,
                    title: selectedOption.title,
                    src: selectedOption.src
                } : null,
                selectedGroup: selectedGroup ? {
                    id: selectedGroup.id,
                    title: selectedGroup.title
                } : null,
                selectedShape: stepData.selectedShape ? {
                    id: stepData.selectedShape.id, 
                    title: stepData.selectedShape.title,
                    type: stepData.selectedShape.type,
                    dimensions: stepData.selectedShape.dimensions
                } : null,
                materialData: selectedOption ? {
                    type: 'predefined_material',
                    src: selectedOption.src,
                    needsProcessing: false,
                    referenceOnly: true,
                    materialType: this.determineMaterialType(selectedOption)
                } : null
            }
        };
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clean up event listeners
        const elementId = this.definition.elements?.[0];
        if (elementId) {
            const element = this.elements.get(elementId);
            if (element) {
                this.detachModificationListener(element);
            }
        }
        
        // Call parent dispose
        super.dispose();
    }

    /**
     * Check if select step has content that needs to be saved
     * 
     * @param {Object} stepData - Current step data
     * @returns {boolean} Whether step has saveable content
     */
    hasSaveableContent(stepData) {
        // Only save if:
        // 1. Step is enabled
        // 2. We have a selected option (texture/material)
        // 3. We have a selected shape
        return stepData && 
            stepData.enabled === true && 
            stepData.selected && 
            stepData.selectedShape;
    }

    /**
     * Create save operations for select content
     * 
     * @param {Object} stepData - Current step data
     * @returns {Promise<Array>} Array of save operation objects
     */
    async createSaveOperations(stepData) {
        const operations = [];
        
        if (!this.hasSaveableContent(stepData)) {
            return operations;
        }
        
        // Generate a unique reference ID for this patch/texture configuration
        const configReferenceId = `patch_config_${this.side}_${this.stepId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        
        // Create configuration save operation
        // This saves the patch/texture selection and shape configuration
        operations.push({
            type: 'config_save',
            referenceId: configReferenceId,
            configType: 'patch_selection',
            metadata: {
                stepId: this.stepId,
                side: this.side,
                fileName: `patch_config_${this.stepId}_${this.side}.json`,
                fileType: 'application/json',
                isPatchConfig: true,
                
                // Patch/texture configuration data
                patchConfiguration: {
                    // Selected material/texture information
                    selectedTexture: {
                        id: stepData.selected.id,
                        title: stepData.selected.title,
                        src: stepData.selected.src,
                        assetPath: `/wp-content/plugins/octo-stc/public/assets/${stepData.selected.src}`
                    },
                    
                    // Selected shape information
                    selectedShape: {
                        id: stepData.selectedShape.id,
                        title: stepData.selectedShape.title,
                        type: stepData.selectedShape.type,
                        dimensions: stepData.selectedShape.dimensions,
                        // Convert dimensions to pixels for production reference
                        pixelDimensions: this.getShapeDimensions(stepData.selectedShape)
                    },
                    
                    // Position and scaling information
                    positioning: {
                        left: stepData.left,
                        top: stepData.top,
                        scaleX: stepData.scaleX,
                        scaleY: stepData.scaleY
                    },
                    
                    // Group information if available
                    ...(stepData.selected.groupId && {
                        textureGroup: {
                            groupId: stepData.selected.groupId,
                            groupTitle: this.findGroupById(stepData.selected.groupId)?.title
                        }
                    }),
                    
                    // Production instructions
                    productionInstructions: {
                        applyAsClippedTexture: true,
                        shapeType: stepData.selectedShape.type,
                        materialType: this.determineMaterialType(stepData.selected),
                        requiresPrintOptimization: true
                    }
                }
            }
        });
        
        // Update step data with reference ID
        const updatedStepData = { ...stepData };
        updatedStepData.configReferenceId = configReferenceId;
        this.setStepData(updatedStepData, false);
        
        return operations;
    }

    /**
     * Find group by ID
     * 
     * @param {string} groupId - Group ID to find
     * @returns {object|null} Found group or null
     */
    findGroupById(groupId) {
        if (!this.definition.optionGroups) return null;
        return this.definition.optionGroups.find(group => group.id === groupId);
    }

    /**
     * Determine material type from selected option
     * 
     * @param {object} selectedOption - Selected texture/material option
     * @returns {string} Material type classification
     */
    determineMaterialType(selectedOption) {
        const optionId = selectedOption.id.toLowerCase();
        
        // Classify based on option ID patterns
        if (optionId.includes('ball')) {
            return 'sports_ball_texture';
        } else if (optionId.includes('jersey')) {
            return 'jersey_fabric_texture';
        } else if (optionId.includes('metal')) {
            return 'metallic_texture';
        } else if (optionId.includes('wood')) {
            return 'wood_texture';
        } else if (optionId.includes('leather')) {
            return 'leather_texture';
        } else {
            return 'standard_texture';
        }
    }

    /**
     * Get priority for patch/texture save operations
     * Patches are decorative elements, so medium priority
     * 
     * @returns {number} Priority level (0-10)
     */
    getSaveOperationsPriority() {
        return 4; // Medium priority - decorative elements
    }

    /**
     * Validate that patch save operations completed successfully
     * 
     * @param {Array} completedOperations - Operations that were processed
     * @returns {boolean} Whether this step's requirements were met
     */
    validateSaveOperations(completedOperations) {
        const stepData = this.getStepData();
        
        // If no patch content, validation passes
        if (!this.hasSaveableContent(stepData)) {
            return true;
        }
        
        // Look for our configuration operation in the completed operations
        const configOperation = completedOperations.find(op => 
            op.sourceStepId === this.stepId && 
            op.sourceSide === this.side &&
            op.type === 'config_save' &&
            op.referenceId === stepData.configReferenceId
        );
        
        if (!configOperation) {
            console.error(`Patch configuration save operation not found for ${this.stepId}`);
            return false;
        }
        
        // Check if the operation was processed successfully
        if (!configOperation.processed) {
            console.error(`Patch configuration save operation failed for ${this.stepId}:`, configOperation.error);
            return false;
        }
        
        // Validate that the configuration contains required data
        const patchConfig = configOperation.metadata?.patchConfiguration;
        if (!patchConfig || !patchConfig.selectedTexture || !patchConfig.selectedShape) {
            console.error(`Invalid patch configuration data for ${this.stepId}`);
            return false;
        }
        
        console.log(`Patch configuration validation passed for ${this.stepId}:`, patchConfig);
        return true;
    }

}